import type {
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig
} from 'axios';
import axios from 'axios';
import { createAuthRefresh as createAuthRefreshInterceptor } from 'axios-auth-refresh';

import { GO_API_URL, GX_API_URL, GX_DATA_URL, GX_GO_TOKEN, GX_INTERNAL_URL } from '@/constants/env';
import {
  getGxTokenFromStorage,
  getTripTokenFromStorage,
  setTripTokenToStorage
} from '@/services/storage';
import { redirectTo } from '@/utils/navigation';

const MAX_RETRY_ATTEMPTS = 3;
let refreshPromise: Promise<string | null> | null = null;

const axiosGxInstance = axios.create({ baseURL: GX_API_URL });
const unauthenticatedGxInstance = axios.create({ baseURL: GX_API_URL });
const axiosGoInstance = axios.create({
  baseURL: GO_API_URL,
  headers: { 'x-gx-go-token': GX_GO_TOKEN }
});
const axiosDataInstance = axios.create({
  baseURL: GX_DATA_URL,
  headers: { 'x-gx-go-token': GX_GO_TOKEN }
});
const axiosInternalInstance = axios.create({
  baseURL: GX_INTERNAL_URL,
  headers: { 'x-gx-go-token': GX_GO_TOKEN }
});

const instances = [axiosGoInstance, axiosDataInstance, axiosInternalInstance, axiosGxInstance];

function attachRequestInterceptor(instance: AxiosInstance) {
  instance.interceptors.request.use((config: InternalAxiosRequestConfig) => {
    const tripToken = getTripTokenFromStorage();
    if (tripToken) {
      config.headers.Authorization = `Bearer ${tripToken}`;
    }
    return config;
  });
}

export const getNewTripToken = async (gxToken: string): Promise<string | null> => {
  let attempts = 0;

  while (attempts < MAX_RETRY_ATTEMPTS) {
    try {
      const url = `/token?token=${encodeURIComponent(gxToken)}`;
      const response = await axiosInternalInstance.get(url, {
        skipAuthRefresh: true
      } as AxiosRequestConfig);
      const token = response.data?.token;
      if (token) {
        setTripTokenToStorage(token);
        return token;
      }
      console.error(`Token refresh attempt ${attempts + 1}: response missing token`);
    } catch (error) {
      console.error(`Token refresh attempt ${attempts + 1} failed:`, error);
    }

    attempts += 1;
  }
  return null;
};

export function getNewTripTokenOnce(gxToken: string): Promise<string | null> {
  refreshPromise ??= getNewTripToken(gxToken).finally(() => {
    refreshPromise = null;
  });
  return refreshPromise;
}

function attachAuthRefreshInterceptor(instance: AxiosInstance) {
  const refreshLogic = async (failedRequest: any) => {
    const gxToken = getGxTokenFromStorage();

    if (!gxToken) {
      redirectTo('/expired');
      throw new Error('No gxToken to refresh');
    }

    const newToken = await getNewTripTokenOnce(gxToken);

    if (!newToken) {
      redirectTo('/expired');
      throw new Error('Could not refresh trip token');
    }

    failedRequest.response.config.headers.Authorization = `Bearer ${newToken}`;
  };

  createAuthRefreshInterceptor(instance, refreshLogic, {
    statusCodes: [401],
    deduplicateRefresh: true
  });
}

function attachFallbackResponseInterceptor(instance: AxiosInstance) {
  instance.interceptors.response.use(
    (response: AxiosResponse) => response,
    error => {
      const isLogin =
        error.config?.url?.includes('/login') || error.config?.url?.includes('/onboarding');
      const gxToken = getGxTokenFromStorage();

      if (!gxToken && !isLogin) {
        if (error.response?.status === 401 || error.response?.status === 403) {
          redirectTo('/expired');
        } else if (error.response?.status !== 404) {
          redirectTo('/notfound');
        }
      }

      return Promise.reject(error);
    }
  );
}

function setupInterceptors(axiosInstances: AxiosInstance[]) {
  for (const instance of axiosInstances) {
    attachRequestInterceptor(instance);
    attachAuthRefreshInterceptor(instance);
    attachFallbackResponseInterceptor(instance);
  }
}

setupInterceptors(instances);

const httpInstances = {
  axiosGoInstance,
  axiosDataInstance,
  axiosInternalInstance,
  axiosGxInstance,
  unauthenticatedGxInstance
};

export default httpInstances;
